import {
	Menu,
	Item,
	Separator,
} from "react-contexify"
import {useDispatch, useSelector} from "react-redux"
import {clearNoteAction, selectNoteAction, setNoteAction, NOTE_ACTIONS} from "../../../reducers/noteActionSlice"
import React, {useMemo, useState} from "react"
import DeleteNoteModal from "./DeleteNoteModal"
import { initializeMovingNote, cancelMovingNote } from "../../../reducers/moveNoteSlice"
import { moveNote, pasteCopy } from "../../../reducers/workspaceNoteSlice"

import {
	Add,
	Close,
	DoDisturb,
	Edit,
	KeyboardDoubleArrowDown,
	KeyboardDoubleArrowLeft,
	ZoomOutMap,
	AddLink,
	CopyAll, NoteAddOutlined, AttachFile, FileCopy
} from "@mui/icons-material"
import { useSnackbar } from "notistack"
import {
	initializeDocAttachmentModal, initializeFileAttachmentModal,
	initializeLinkAttachmentModal
} from "../../../reducers/noteAttachmentsSlice"
import NoteAttachmentModal from "./Attachments/NoteAttachmentModals/NoteAttachmentModal"
import {selectTargetNote} from "../../../reducers/targetNoteSlice"
import { cancelCopyNote, initializeCopyNote } from "../../../reducers/copyNoteSlice"
import { selectCopyNote } from "../../../reducers/copyNoteSlice"
import {
	makeSelectNoteCanBeEdited,
	selectAllowedEditNoteIds
} from "../../../reducers/noteRestrictionsSlice"
import {Tooltip} from "@mui/material"
import {useWorkspaceContext} from "../../../pages/Dashboard"


const NoteMenu =  React.memo(function NoteMenu({
	permissions,
	setShowingContext,
	hideAll
}) {
	const dispatch = useDispatch()
	const { workspaceId, compositeKey } = useWorkspaceContext()
	const noteAction = useSelector((state) => selectNoteAction(state, workspaceId))
	const copyNote = useSelector(selectCopyNote)
	const { enqueueSnackbar } = useSnackbar()
	const targetNote = useSelector((state) => selectTargetNote(state, workspaceId))
	const [movingNote, setMovingNote] = useState(null)
	const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState(false)
	const [ noteToDelete, setNoteToDelete ] = useState(null)
	const noteCanBeEditedSelector = useMemo(
		() => makeSelectNoteCanBeEdited(workspaceId),
		[workspaceId]
	)
	const noteCanBeEdited = useSelector(noteCanBeEditedSelector)

	const checkIfNoteCanMove = (noteBeingMoved, receivingNote) => {
		const noteIsSelf = receivingNote.id === noteBeingMoved.id
		const receivingPath = receivingNote.path.split(".")
		const parentIsChild = receivingPath.includes(`${noteBeingMoved.id}`)

		if (noteIsSelf) {
			enqueueSnackbar("Cannot move a note to itself.", { variant: "info" })
		} else if (parentIsChild) {
			enqueueSnackbar("Cannot move a parent note inside one of its children.", {
				variant: "info",
			})
		}

		return !noteIsSelf && !parentIsChild
	}

	const writeRestrictions = useSelector((state) => selectAllowedEditNoteIds(state, workspaceId))
	const checkIfUserCanCreateRootNotes = () => {
		if(permissions.admin) {
			return true
		}
		const targetNoteIsRoot = targetNote.parentId === null

		return (targetNoteIsRoot && writeRestrictions.length > 0) === false
	}

	const handleMouseLeave = (e) => {
		const relatedTarget = e.relatedTarget
		const mouseLeavingToMenuIcon =
			relatedTarget instanceof Element // related target is a valid HTML Element and not null
			&& relatedTarget.classList.contains("MuiSvgIcon-root") // related target is SVG
			|| relatedTarget.tagName === "path" // related target is child of SVG
		if(!mouseLeavingToMenuIcon){
			hideAll()
		}
	}

	const handleItemClick = async ({ data }) => {
		hideAll()
		switch (data) {
			case "add-child":
				dispatch(setNoteAction({ noteId: targetNote.id, action: NOTE_ACTIONS.addChild, workspaceId }))
				break
			case "add-sibling":
				if (targetNote.parentId) {
					dispatch(
						setNoteAction({ noteId: targetNote.id, action: NOTE_ACTIONS.addSibling, workspaceId })
					)
				} else {
					dispatch(setNoteAction({ noteId: targetNote.id, action: NOTE_ACTIONS.addRoot, workspaceId }))
				}
				break
			case "edit":
				dispatch(setNoteAction({ noteId: targetNote.id, action: NOTE_ACTIONS.editNote, workspaceId }))
				break
			case "move":
				dispatch(initializeMovingNote({targetNote, workspaceId}))
				setMovingNote(targetNote)
				break
			case "move-as-child":
				if (checkIfNoteCanMove(movingNote, targetNote)) {
					dispatch(moveNote({movingNote, receivingNoteId: targetNote.id, receivingPath: targetNote.path, workspaceId}))
					setMovingNote(null)
				}
				break
			case "move-as-sibling":
				if (checkIfNoteCanMove(movingNote, targetNote)) {
					dispatch(moveNote({movingNote, receivingNoteId: targetNote.parentId, receivingPath: targetNote.path, workspaceId}))
					setMovingNote(null)
				}
				break
			case "cancel-move":
				dispatch(cancelMovingNote({movingNote, workspaceId}))
				setMovingNote(null)
				break
			case "paste-as-child":
				dispatch(pasteCopy({
					pasteAsSibling: false,
					pasteObject: copyNote,
					targetNote,
				}))
				break
			case "paste-as-sibling":
				dispatch(pasteCopy({
					pasteAsSibling: true,
					pasteObject: copyNote,
					targetNote,
				}))
				break
			case "cancel-copy":
				dispatch(cancelCopyNote(workspaceId))
				break
			case "cancel-edit":
				dispatch(clearNoteAction(workspaceId))
				break
			case "delete":
				setNoteToDelete(targetNote.id)
				setIsDeleteModalOpen(true)
				break
			case "attach-link":
				dispatch(initializeLinkAttachmentModal({note: targetNote, actionType: "create-link"}))
				break
			case "attach-file":
				dispatch(initializeFileAttachmentModal({note: targetNote, actionType: "create-file"}))
				break
			case "create-doc":
				dispatch(initializeDocAttachmentModal({ note: targetNote, actionType: "create-doc" }))
				break
			case "copy":
				dispatch(initializeCopyNote({
					id: targetNote.id,
					noteToPaste: targetNote,
					includeAttachments: false,
					workspaceId
				}))
				break
			case "copy-with-attachments":
				dispatch(initializeCopyNote({
					id: targetNote.id,
					noteToPaste: targetNote,
					includeAttachments: true,
					workspaceId
				}))
				break
			default:
				break
		}
	}

	if (!permissions.write || (!permissions.admin && noteCanBeEdited === false)) {
		//TODO: try taking the text size down to 12pt and writing "You do not have permission to edit this note"
		return (
			<Menu
				id={compositeKey}
				onVisibilityChange={setShowingContext}
				animation={{
					enter: "fade",
					exit: false
				}}
				onMouseLeave={() => hideAll()}
			>
				<Item disabled>You Do Not Have Permission</Item>
			</Menu>
		)
	}

	if( [NOTE_ACTIONS.editNote, NOTE_ACTIONS.addChild, NOTE_ACTIONS.addSibling, NOTE_ACTIONS.addRoot].includes(noteAction.action) ) {
		return null
	}

	if (copyNote.id >= 1) {
		return (
			<Menu
				id={compositeKey}
				onVisibilityChange={setShowingContext}
				animation={{
					enter: "fade",
					exit: false
				}}
				onMouseLeave={handleMouseLeave}
			>
				<NoteMenuDisabledTooltip
					ignoreCondition={true}
					title={"Paste copied note(s) as a child of this note"}
				>
					<Item data="paste-as-child" onClick={handleItemClick}>
						<KeyboardDoubleArrowDown
							fontSize={"small"}
							color={"primary"}
							sx={{ paddingRight: "5px" }}
						/>
						Paste as Child
					</Item>
				</NoteMenuDisabledTooltip>
				<NoteMenuDisabledTooltip
					ignoreCondition={checkIfUserCanCreateRootNotes() === true}
					title={"Paste copied note(s) as a sibling of this note."}
				>
					<Item data="paste-as-sibling" onClick={handleItemClick} disabled={() => checkIfUserCanCreateRootNotes() === false}>
						<KeyboardDoubleArrowLeft
							fontSize={"small"}
							sx={{ color: "#33C73B", paddingRight: "5px" }}
						/>
						Paste as Sibling
					</Item>
				</NoteMenuDisabledTooltip>
				<Separator />
				<Item data="cancel-copy" onClick={handleItemClick}>
					<DoDisturb
						fontSize={"small"}
						sx={{ color: "#C72D30", paddingRight: "5px" }}
					/>
					Cancel
				</Item>
			</Menu>
		)
	}

	if (movingNote) {
		return (
			<Menu
				id={compositeKey}
				onVisibilityChange={setShowingContext}
				animation={{
					enter: "fade",
					exit: false
				}}
				onMouseLeave={handleMouseLeave}
			>
				<Item data="move-as-child" onClick={handleItemClick}>
					<KeyboardDoubleArrowDown
						fontSize={"small"}
						color={"primary"}
						sx={{ paddingRight: "5px" }}
					/>
					Paste as child
				</Item>
				<NoteMenuDisabledTooltip ignoreCondition={checkIfUserCanCreateRootNotes() === true}>
					<Item data="move-as-sibling" onClick={handleItemClick} disabled={() => checkIfUserCanCreateRootNotes() === false}>
						<KeyboardDoubleArrowLeft
							fontSize={"small"}
							sx={{ color: "#33C73B", paddingRight: "5px" }}
						/>
						Paste as Sibling
					</Item>
				</NoteMenuDisabledTooltip>
				<Separator />
				<Item data="cancel-move" onClick={handleItemClick}>
					<DoDisturb
						fontSize={"small"}
						sx={{ color: "#C72D30", paddingRight: "5px" }}
					/>
					Cancel
				</Item>
			</Menu>
		)
	}

	return (
		<>
			<Menu
				id={compositeKey}
				animation={{
					enter: "fade",
					exit: false
				}}
				onMouseLeave={handleMouseLeave}
				onVisibilityChange={setShowingContext}
				style={{ minWidth: "150px" }}
			>
				<NoteMenuDisabledTooltip
					ignoreCondition={true}
					title={"Create a sub-note nested under this parent item"}
				>
					<Item data="add-child" onClick={handleItemClick}>
						<Add
							fontSize={"small"}
							sx={{ color: "#33C73B", paddingRight: "5px" }}
						/>
						Add child
					</Item>
				</NoteMenuDisabledTooltip>
				<NoteMenuDisabledTooltip
					ignoreCondition={checkIfUserCanCreateRootNotes() === true}
					title={"Create a note at the same hierarchy level."}
				>
					<Item data="add-sibling" onClick={handleItemClick} disabled={() => checkIfUserCanCreateRootNotes() === false}>
						<Add
							fontSize={"small"}
							sx={{ color: "#33C73B", paddingRight: "5px" }}
						/>
						Add sibling
					</Item>
				</NoteMenuDisabledTooltip>
				<Separator />
				<NoteMenuDisabledTooltip
					ignoreCondition={true}
					title={"Edit this note"}
				>
					<Item data="edit" onClick={handleItemClick}>
						<Edit
							fontSize={"small"}
							sx={{ color: "#c78f33", paddingRight: "5px" }}
						/>
						Edit note
					</Item>
				</NoteMenuDisabledTooltip>
				<NoteMenuDisabledTooltip
					ignoreCondition={checkIfUserCanCreateRootNotes() === true}
					title={"Transfer this note"}
				>
					<Item data="move" onClick={handleItemClick} disabled={() => checkIfUserCanCreateRootNotes() === false}>
						<ZoomOutMap
							fontSize={"small"}
							sx={{ color: "#6985cb", paddingRight: "5px" }}
						/>
						Move note
					</Item>
				</NoteMenuDisabledTooltip>
				<Separator />
				<NoteMenuDisabledTooltip
					ignoreCondition={true}
					title={"Copy this note and all descendants. No citations will be included."}
				>
					<Item data="copy" onClick={handleItemClick}>
						<CopyAll
							fontSize="small"
							sx={{ color: "#6985cb", paddingRight: "5px" }}
						/>
						Copy notes W/O
					</Item>
				</NoteMenuDisabledTooltip>
				<NoteMenuDisabledTooltip
					ignoreCondition={true}
					title={"Copy this note and all descendants. All citations will be included."}
				>
					<Item data="copy-with-attachments" onClick={handleItemClick}>
						<FileCopy
							fontSize="small"
							sx={{ color: "#6985cb", paddingRight: "5px" }}
						/>
						Copy notes W/
					</Item>
				</NoteMenuDisabledTooltip>
				<Separator />
				<Item data={"attach-link"} onClick={handleItemClick}>
					<AddLink
						fontSize={"small"}
						sx={{ color: "#656565", paddingRight: "5px" }}
					/>
					Attach Link
				</Item>
				<Item data={"attach-file"} onClick={handleItemClick}>
					<AttachFile
						fontSize={"small"}
						sx={{ color: "#656565", paddingRight: "5px" }}
					/>
					Attach File
				</Item>
				{ permissions.admin && (
					<Tooltip
						title={"Create a new Document Attachment & open the Refzoom Document Viewer"}
						enterDelay={300}
						enterNextDelay={1000}
						leaveDelay={0}
						placement={"right"}
						arrow
					>
						<div>
							<Item data={"create-doc"} onClick={handleItemClick}>
								<NoteAddOutlined
									fontSize={"small"}
									sx={{ color: "#656565", paddingRight: "5px" }}
								/>
								Create New Document
							</Item>
						</div>
					</Tooltip>
				)}
				<Separator />
				<NoteMenuDisabledTooltip ignoreCondition={checkIfUserCanCreateRootNotes() === true}>
					<Item data="delete" onClick={handleItemClick} disabled={() => checkIfUserCanCreateRootNotes() === false}>
						<Close
							fontSize={"small"}
							sx={{ color: "#C72D30", paddingRight: "5px" }}
						/>
						Delete note
					</Item>
				</NoteMenuDisabledTooltip>
			</Menu>
			<Menu id={"portal_menu"}>
				<Item data={"copy-portal-note"} onClick={handleItemClick}>
					Export to Workspace
				</Item>
			</Menu>
			<NoteAttachmentModal/>
			<DeleteNoteModal
				open={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				workspaceId={workspaceId}
				noteId={noteToDelete}>
			</DeleteNoteModal>
		</>
	)
})

export default NoteMenu

/**
 * A modified version of the MUI Tooltip which will not appear if the ignoreCondition evals to true
 *
 * @param ignoreCondition {boolean} - statement that evaluates to a boolean
 * @param title {string} - string to go into the MUI Tooltip title prop IF an ignore condition isn't met
 * @param children {JSX.Element} - passed via the slot
 * @returns {JSX.Element} - Component wrapped in the conditional tooltip
 */
function NoteMenuDisabledTooltip({ignoreCondition, title, children}) {
	return (
		<Tooltip
			title={ignoreCondition ?
				title
				:
				"You do not have permission to modify the top level of this workspace"
			}
			//disableHoverListener={ignoreCondition}
			enterDelay={300}
			enterNextDelay={1000}
			leaveDelay={0}
			placement={"right"}
			arrow
		>
			<div>
				{children}
			</div>
		</Tooltip>
	)
}
